import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { Check, Pencil, X } from 'lucide-react';
import { useState } from 'react';

interface EditableFieldProps {
    value: string;
    onSave: (value: string) => void;
    label?: string;
    type?: 'text' | 'textarea' | 'date';
    placeholder?: string;
    className?: string;
    multiline?: boolean;
    readOnly?: boolean;
}

export function EditableField({
    value,
    onSave,
    label,
    type = 'text',
    placeholder,
    className,
    multiline = false,
    readOnly = false,
}: EditableFieldProps) {
    const { t } = useTranslations();
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);

    const handleSave = () => {
        onSave(editValue);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditValue(value);
        setIsEditing(false);
    };

    if (isEditing && !readOnly) {
        return (
            <div className={cn('w-full min-w-0 space-y-2', className)}>
                {label && <label className="text-sm font-medium">{label}</label>}
                <div className="flex gap-2">
                    {multiline || type === 'textarea' ? (
                        <Textarea
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            placeholder={placeholder}
                            className="min-h-[80px] w-[30vw] min-w-0 flex-1"
                            autoFocus
                        />
                    ) : (
                        <Input
                            type={type}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            placeholder={placeholder}
                            className="min-w-0 flex-1"
                            autoFocus
                        />
                    )}
                    <Button type="button" size="icon" variant="outline" onClick={handleSave} className="shrink-0">
                        <Check className="h-4 w-4" />
                    </Button>
                    <Button type="button" size="icon" variant="outline" onClick={handleCancel} className="shrink-0">
                        <X className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className={cn('group relative w-full min-w-0', className)}>
            {label && <label className="text-muted-foreground mb-1 block text-sm font-medium">{label}</label>}
            <div className="flex items-center gap-2">
                <span className={cn('flex-1', !value && 'text-muted-foreground italic')}>
                    {value || placeholder || t('work_orders.show.sidebar.no_date_provided', 'Not set')}
                </span>
                {!readOnly && (
                    <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                        onClick={() => setIsEditing(true)}
                    >
                        <Pencil className="h-3.5 w-3.5" />
                    </Button>
                )}
            </div>
        </div>
    );
}
